import { defineQueries, defineQuery } from '@rocicorp/zero'
import { z } from 'zod'

import { schema, zql } from '../../zero-schema.gen.ts'
import './zero-default-types.ts'
import type { ZeroQueryContext } from './zero-default-types.ts'

const NO_ACCESS = '__zero_no_access__'

function userIdForQuery(ctx: ZeroQueryContext | undefined) {
  return ctx?.user.id ?? NO_ACCESS
}

function workspaceIdForQuery(
  ctx: ZeroQueryContext | undefined,
  workspaceId: string,
) {
  if (ctx === undefined || workspaceId.length === 0) {
    return NO_ACCESS
  }
  return workspaceId
}

function tokenForQuery(ctx: ZeroQueryContext | undefined, token: string) {
  if (ctx === undefined || token.length === 0) {
    return NO_ACCESS
  }
  return token
}

/**
 * Terminal run statuses excluded from `implementations.activeDetails`. A task
 * whose current run is in any other status has its full detail preloaded
 * locally (see `usePreloadActiveTaskDetails`).
 */
export const inactiveTaskRunStatuses: ReadonlyArray<'completed' | 'cancelled'> =
  ['completed', 'cancelled']

/** Recent reports shown on an item. */
export const FRICTION_DETAIL_OCCURRENCES = 10

export const queries = defineQueries({
  auth: {
    /**
     * Mirrors `auth.me` (tRPC): current user, profile locale, workspaces +
     * per-workspace dismissed onboarding cards.
     */
    me: defineQuery(({ ctx }) => {
      return zql.users
        .where('id', userIdForQuery(ctx))
        .related('profile', (q) => q.one())
        .related('memberships', (q) =>
          q.related('workspace', (w) => w).related('settings', (s) => s.one()),
        )
        .one()
    }),
    accounts: defineQuery(({ ctx }) => {
      return zql.accounts.where('userId', userIdForQuery(ctx))
    }),
  },
  workspaces: {
    /**
     * Mirrors former tRPC `workspaces.bySlug` (membership enforced in mapper).
     */
    bySlug: defineQuery(z.object({ slug: z.string() }), ({ ctx, args }) => {
      const userId = userIdForQuery(ctx)
      const slug =
        ctx !== undefined && args.slug.length > 0 ? args.slug : NO_ACCESS

      return zql.workspaces
        .where('slug', slug)
        .whereExists('members', (q) =>
          q.where('userId', userId).where('status', 'active'),
        )
        .related('members', (q) => q.related('user', (u) => u.one()))
        .related('subscription', (q) => q.one())
        .related('tags', (q) => q)
        .related('workspaceIntegrations', (q) => q)
        .related('githubInstallations', (q) => q)
        .one()
    }),
  },
  workspaceMembers: {
    members: defineQuery(
      z.object({ workspaceId: z.string() }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)

        return zql.workspaceMembers
          .where('workspaceId', workspaceId)
          .whereExists('workspace', (q) =>
            q.whereExists('members', (m) =>
              m
                .where('userId', userId)
                .where('status', 'active')
                .where(({ cmp, or }) =>
                  or(cmp('role', 'owner'), cmp('role', 'admin')),
                ),
            ),
          )
          .related('user', (u) => u.one())
      },
    ),
    invitations: defineQuery(
      z.object({ workspaceId: z.string() }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)

        return zql.workspaceInvitations
          .where('workspaceId', workspaceId)
          .where('accepted', false)
          .whereExists('workspace', (q) =>
            q.whereExists('members', (m) =>
              m
                .where('userId', userId)
                .where('status', 'active')
                .where(({ cmp, or }) =>
                  or(cmp('role', 'owner'), cmp('role', 'admin')),
                ),
            ),
          )
          .related('user', (u) => u.one())
      },
    ),
    notificationSettings: defineQuery(
      z.object({ workspaceId: z.string() }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)

        return zql.workspaceMembers
          .where('userId', userId)
          .where('workspaceId', workspaceId)
          .where('status', 'active')
          .related('settings', (q) => q.one())
          .one()
      },
    ),
    invitation: defineQuery(
      z.object({ token: z.string() }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const email = ctx?.user.email ?? NO_ACCESS
        const token = tokenForQuery(ctx, args.token)

        return zql.workspaceInvitations
          .where('id', token)
          .where('accepted', false)
          .where(({ cmp, or }) =>
            or(cmp('userId', userId), cmp('email', email)),
          )
          .related('workspace', (w) => w.one())
          .related('inviter', (u) => u.one())
          .one()
      },
    ),
  },
  notifications: {
    inbox: defineQuery(
      z.object({ workspaceId: z.string() }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)

        return zql.notifications
          .where('workspaceId', workspaceId)
          .where('targetType', 'user')
          .where('targetId', userId)
          .whereExists('workspace', (q) =>
            q.whereExists('members', (m) =>
              m.where('userId', userId).where('status', 'active'),
            ),
          )
      },
    ),
  },
  integrations: {
    catalog: defineQuery(() => zql.integrations),
    list: defineQuery(
      z.object({ workspaceId: z.string() }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)

        return zql.workspaceIntegrations
          .where('workspaceId', workspaceId)
          .whereExists('workspace', (q) =>
            q.whereExists('members', (m) =>
              m.where('userId', userId).where('status', 'active'),
            ),
          )
          .related('integration', (q) => q.one())
      },
    ),
    byIntegrationId: defineQuery(
      z.object({ workspaceId: z.string(), integrationId: z.string() }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)
        const integrationId =
          ctx !== undefined && args.integrationId.length > 0
            ? args.integrationId
            : NO_ACCESS

        return zql.workspaceIntegrations
          .where('workspaceId', workspaceId)
          .where('integrationId', integrationId)
          .whereExists('workspace', (q) =>
            q.whereExists('members', (m) =>
              m.where('userId', userId).where('status', 'active'),
            ),
          )
          .related('integration', (q) => q.one())
          .one()
      },
    ),
  },
  blueprints: {
    list: defineQuery(
      z.object({ workspaceId: z.string() }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)

        return zql.blueprints
          .where('active', true)
          .where(({ cmp, or, and, exists }) =>
            or(
              cmp('workspaceId', 'IS', null),
              and(
                cmp('workspaceId', workspaceId),
                exists('workspace', (q) =>
                  q.whereExists('members', (m) =>
                    m.where('userId', userId).where('status', 'active'),
                  ),
                ),
              ),
            ),
          )
          .related('steps', (q) => q.orderBy('order', 'asc'))
          .orderBy('displayOrder', 'asc')
          .orderBy('name', 'asc')
      },
    ),
  },
  github: {
    repositories: defineQuery(
      z.object({ workspaceId: z.string() }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)

        return zql.repositories
          .where('workspaceId', workspaceId)
          .where('state', 'active')
          .whereExists('workspace', (w) =>
            w.whereExists('members', (m) =>
              m.where('userId', userId).where('status', 'active'),
            ),
          )
          .orderBy('lastUsedAt', 'desc')
          .orderBy('fullName', 'asc')
      },
    ),
    installations: defineQuery(
      z.object({ workspaceId: z.string() }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)

        return zql.githubInstallations
          .where('workspaceId', workspaceId)
          .whereExists('workspace', (w) =>
            w.whereExists('members', (m) =>
              m.where('userId', userId).where('status', 'active'),
            ),
          )
          .orderBy('createdAt', 'desc')
      },
    ),
  },
  implementations: {
    byId: defineQuery(z.object({ id: z.string() }), ({ ctx, args }) => {
      const userId = userIdForQuery(ctx)
      const id = tokenForQuery(ctx, args.id)

      return zql.implementations
        .where('id', id)
        .whereExists('workspace', (w) =>
          w.whereExists('members', (m) =>
            m.where('userId', userId).where('status', 'active'),
          ),
        )
        .related('user', (q) => q.one())
        .related('brief', (q) => q.one())
        .related('currentRun', (q) =>
          q
            .one()
            .related('runRepos', (rr) => rr)
            .related('workflowState', (state) => state.one())
            .related('executions', (execution) =>
              execution
                .related('stepExecutions', (stepExecution) =>
                  stepExecution.orderBy('sequence', 'asc'),
                )
                .orderBy('sequence', 'asc'),
            )
            .related('sandbox', (s) => s.one()),
        )
        .related('runs', (q) => q.orderBy('createdAt', 'desc'))
        .related('steps', (q) => q.orderBy('order', 'asc'))
        .related('runMessages', (q) => q.orderBy('createdAt', 'asc'))
        .related('blueprint', (q) => q.one())
        .one()
    }),
    /**
     * Full task detail (same related tree as `byId`) for every active task —
     * any non-archived task whose current run is not in a terminal
     * completed/cancelled state. Preloaded once so switching between active
     * tasks renders instantly from local data.
     */
    activeDetails: defineQuery(
      z.object({
        workspaceId: z.string(),
        limit: z.number().min(1).max(50).default(20),
      }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)

        return zql.implementations
          .where('workspaceId', workspaceId)
          .where('archivedAt', 'IS', null)
          .whereExists('workspace', (w) =>
            w.whereExists('members', (m) =>
              m.where('userId', userId).where('status', 'active'),
            ),
          )
          .whereExists('currentRun', (r) =>
            r.where('status', 'NOT IN', inactiveTaskRunStatuses),
          )
          .related('user', (q) => q.one())
          .related('brief', (q) => q.one())
          .related('currentRun', (q) =>
            q
              .one()
              .related('runRepos', (rr) => rr)
              .related('workflowState', (state) => state.one())
              .related('executions', (execution) =>
                execution
                  .related('stepExecutions', (stepExecution) =>
                    stepExecution.orderBy('sequence', 'asc'),
                  )
                  .orderBy('sequence', 'asc'),
              )
              .related('sandbox', (s) => s.one()),
          )
          .related('runs', (q) => q.orderBy('createdAt', 'desc'))
          .related('steps', (q) => q.orderBy('order', 'asc'))
          .related('runMessages', (q) => q.orderBy('createdAt', 'asc'))
          .related('blueprint', (q) => q.one())
          .orderBy('updatedAt', 'desc')
          .limit(args.limit)
      },
    ),
    listActive: defineQuery(
      z.object({
        workspaceId: z.string(),
        limit: z.number().min(1).max(50).default(10),
      }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)
        const recentFailureCutoff = Date.now() - 3 * 24 * 60 * 60 * 1000

        return zql.implementations
          .where('workspaceId', workspaceId)
          .where('archivedAt', 'IS', null)
          .whereExists('workspace', (w) =>
            w.whereExists('members', (m) =>
              m.where('userId', userId).where('status', 'active'),
            ),
          )
          .where(({ cmp, and, or, exists }) =>
            or(
              exists('currentRun', (r) =>
                r.where(({ cmp: runCmp, or: runOr }) =>
                  runOr(
                    runCmp('status', 'pending'),
                    runCmp('status', 'queued'),
                    runCmp('status', 'running'),
                  ),
                ),
              ),
              and(
                cmp('updatedAt', '>=', recentFailureCutoff),
                exists('currentRun', (r) => r.where('status', 'failed')),
              ),
              exists('currentRun', (r) =>
                r
                  .where('status', 'completed')
                  .whereExists('runRepos', (rr) =>
                    rr.where('prStatus', 'open'),
                  ),
              ),
            ),
          )
          .related('brief', (q) => q.one())
          .related('currentRun', (q) =>
            q
              .one()
              .related('runRepos', (rr) => rr)
              .related('workflowState', (state) => state.one())
              .related('executions', (execution) =>
                execution.orderBy('sequence', 'desc').limit(1),
              ),
          )
          .related('steps', (q) => q.orderBy('order', 'asc'))
          .orderBy('updatedAt', 'desc')
          .limit(args.limit)
      },
    ),
    list: defineQuery(
      z.object({
        briefId: z.string(),
        limit: z.number().min(1).max(200).default(20),
      }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const briefId = tokenForQuery(ctx, args.briefId)

        return zql.implementations
          .where('briefId', briefId)
          .whereExists('workspace', (w) =>
            w.whereExists('members', (m) =>
              m.where('userId', userId).where('status', 'active'),
            ),
          )
          .related('currentRun', (q) =>
            q
              .one()
              .related('runRepos', (rr) => rr)
              .related('workflowState', (state) => state.one())
              .related('executions', (execution) =>
                execution.orderBy('sequence', 'desc').limit(1),
              ),
          )
          .orderBy('createdAt', 'desc')
          .limit(args.limit)
      },
    ),
    listAll: defineQuery(
      z.object({
        workspaceId: z.string(),
        filter: z.enum(['active', 'archived']).default('active'),
        limit: z.number().min(1).max(200).default(20),
      }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)

        return zql.implementations
          .where('workspaceId', workspaceId)
          .where(({ cmp, and, not }) =>
            args.filter === 'archived'
              ? cmp('archivedAt', 'IS NOT', null)
              : and(
                  cmp('archivedAt', 'IS', null),
                  not(cmp('currentRunId', 'IS', null)),
                ),
          )
          .whereExists('workspace', (w) =>
            w.whereExists('members', (m) =>
              m.where('userId', userId).where('status', 'active'),
            ),
          )
          .whereExists('currentRun', (r) => r)
          .related('currentRun', (q) =>
            q
              .one()
              .related('runRepos', (rr) => rr)
              .related('workflowState', (state) => state.one())
              .related('executions', (execution) =>
                execution.orderBy('sequence', 'desc').limit(1),
              ),
          )
          .related('steps', (q) => q.orderBy('order', 'asc'))
          .orderBy('updatedAt', 'desc')
          .limit(args.limit)
      },
    ),
    uiComments: defineQuery(
      z.object({
        implementationId: z.string(),
        status: z
          .enum(['open', 'in_progress', 'resolved', 'ignored'])
          .optional(),
        limit: z.number().min(1).max(200).default(50),
      }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const implementationId = tokenForQuery(ctx, args.implementationId)

        return zql.implementationUIComments
          .where('implementationId', implementationId)
          .where('status', args.status)
          .whereExists('implementation', (i) =>
            i.whereExists('workspace', (w) =>
              w.whereExists('members', (m) =>
                m.where('userId', userId).where('status', 'active'),
              ),
            ),
          )
          .orderBy('createdAt', 'asc')
          .limit(args.limit)
      },
    ),
    steps: defineQuery(
      z.object({ implementationId: z.string() }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const implementationId = tokenForQuery(ctx, args.implementationId)

        return zql.runSteps
          .whereExists('run', (r) =>
            r
              .where('implementationId', implementationId)
              .whereExists('implementation', (i) =>
                i.whereExists('workspace', (w) =>
                  w.whereExists('members', (m) =>
                    m.where('userId', userId).where('status', 'active'),
                  ),
                ),
              ),
          )
          .orderBy('order', 'asc')
      },
    ),
  },
  sessions: {
    byId: defineQuery(z.object({ id: z.string() }), ({ ctx, args }) => {
      const userId = userIdForQuery(ctx)
      const id = tokenForQuery(ctx, args.id)

      return (
        zql.runs
          .where('id', id)
          .where('type', 'collaborative')
          .whereExists('workspace', (w) =>
            w.whereExists('members', (m) =>
              m.where('userId', userId).where('status', 'active'),
            ),
          )
          .related('runRepos', (rr) => rr)
          .related('runMessages', (q) =>
            q.orderBy('createdAt', 'asc').related('user', (u) => u.one()),
          )
          .related('runTurns', (q) => q.orderBy('createdAt', 'asc'))
          .related('runParticipants', (q) => q.related('user', (u) => u.one()))
          // Sandbox provider/lifecycle: an availability hint for the Daytona-only
          // Desktop view. The server re-authorizes every desktop connection, so
          // this is a UI affordance, not authorization.
          .related('sandbox', (s) => s.one())
          .one()
      )
    }),
    list: defineQuery(
      z.object({
        workspaceId: z.string(),
        filter: z.enum(['active', 'archived']).default('active'),
        participatingOnly: z.boolean().default(false),
      }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)

        let query = zql.runs
          .where('workspaceId', workspaceId)
          .where('type', 'collaborative')
          .where(({ cmp }) =>
            args.filter === 'archived'
              ? cmp('archivedAt', 'IS NOT', null)
              : cmp('archivedAt', 'IS', null),
          )
          .whereExists('workspace', (w) =>
            w.whereExists('members', (m) =>
              m.where('userId', userId).where('status', 'active'),
            ),
          )

        if (args.participatingOnly) {
          query = query.whereExists('runParticipants', (p) =>
            p.where('userId', userId),
          )
        }

        return query
          .related('runRepos', (rr) => rr)
          .related('runParticipants', (q) => q.related('user', (u) => u.one()))
          .orderBy('lastActivityAt', 'desc')
      },
    ),
  },
  runs: {
    listReadyForReview: defineQuery(
      z.object({
        workspaceId: z.string(),
        limit: z.number().min(1).max(50).default(10),
      }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)

        return zql.runs
          .where('workspaceId', workspaceId)
          .where('archivedAt', 'IS', null)
          .whereExists('runRepos', (rr) => rr.where('prStatus', 'open'))
          .whereExists('workspace', (w) =>
            w.whereExists('members', (m) =>
              m.where('userId', userId).where('status', 'active'),
            ),
          )
          .related('runRepos', (rr) => rr)
          .related('implementation', (q) => q.one())
          .orderBy('updatedAt', 'desc')
          .limit(args.limit)
      },
    ),
  },
  automations: {
    list: defineQuery(
      z.object({
        workspaceId: z.string(),
        limit: z.number().min(1).max(200).default(20),
      }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)

        return zql.automations
          .where('workspaceId', workspaceId)
          .whereExists('workspace', (w) =>
            w.whereExists('members', (m) =>
              m.where('userId', userId).where('status', 'active'),
            ),
          )
          .related('schedules', (q) => q)
          .related('repositories', (q) => q.orderBy('position', 'asc'))
          .orderBy('createdAt', 'desc')
          .limit(args.limit)
      },
    ),
    runs: defineQuery(
      z.object({
        automationId: z.string(),
        limit: z.number().min(1).max(100).default(20),
      }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const automationId = tokenForQuery(ctx, args.automationId)

        return zql.implementations
          .where('automationId', automationId)
          .whereExists('workspace', (w) =>
            w.whereExists('members', (m) =>
              m.where('userId', userId).where('status', 'active'),
            ),
          )
          .related('currentRun', (q) =>
            q
              .one()
              .related('runRepos', (rr) => rr)
              .related('workflowState', (state) => state.one())
              .related('executions', (execution) =>
                execution.orderBy('sequence', 'desc').limit(1),
              ),
          )
          .orderBy('createdAt', 'desc')
          .limit(args.limit)
      },
    ),
    byId: defineQuery(z.object({ id: z.string() }), ({ ctx, args }) => {
      const userId = userIdForQuery(ctx)
      const id = tokenForQuery(ctx, args.id)

      return zql.automations
        .where('id', id)
        .whereExists('workspace', (w) =>
          w.whereExists('members', (m) =>
            m.where('userId', userId).where('status', 'active'),
          ),
        )
        .related('schedules', (q) => q)
        .related('repositories', (q) => q.orderBy('position', 'asc'))
        .one()
    }),
  },
  secrets: {
    list: defineQuery(
      z.object({ workspaceId: z.string() }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)

        return zql.workspaceRuntimeSecrets
          .where('workspaceId', workspaceId)
          .whereExists('workspace', (w) =>
            w.whereExists('members', (m) =>
              m
                .where('userId', userId)
                .where('status', 'active')
                .where(({ cmp, or }) =>
                  or(cmp('role', 'owner'), cmp('role', 'admin')),
                ),
            ),
          )
          .orderBy('createdAt', 'desc')
      },
    ),
  },
  knowledge: {
    list: defineQuery(
      z.object({
        workspaceId: z.string(),
        type: z
          .enum([
            'decision',
            'bugfix',
            'feature',
            'refactor',
            'discovery',
            'change',
          ])
          .optional(),
        project: z.string().optional(),
        implementationId: z.string().optional(),
        search: z.string().optional(),
        limit: z.number().min(1).max(500).default(20),
      }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)
        const search =
          args.search && args.search.trim().length > 0
            ? `%${args.search.trim()}%`
            : undefined

        return zql.knowledgeObservations
          .where('workspaceId', workspaceId)
          .where('type', args.type)
          .where('project', args.project)
          .where('implementationId', args.implementationId)
          .where(({ cmp, or }) =>
            search === undefined
              ? cmp('id', 'IS NOT', null)
              : or(
                  cmp('title', 'ILIKE', search),
                  cmp('narrative', 'ILIKE', search),
                ),
          )
          .whereExists('workspace', (w) =>
            w.whereExists('members', (m) =>
              m.where('userId', userId).where('status', 'active'),
            ),
          )
          .orderBy('createdAt', 'desc')
          .limit(args.limit)
      },
    ),
    byId: defineQuery(
      z.object({ id: z.string(), workspaceId: z.string() }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)
        const id = tokenForQuery(ctx, args.id)

        return zql.knowledgeObservations
          .where('id', id)
          .where('workspaceId', workspaceId)
          .whereExists('workspace', (w) =>
            w.whereExists('members', (m) =>
              m.where('userId', userId).where('status', 'active'),
            ),
          )
          .one()
      },
    ),
    byImplementation: defineQuery(
      z.object({
        implementationId: z.string(),
        workspaceId: z.string(),
        type: z
          .enum([
            'decision',
            'bugfix',
            'feature',
            'refactor',
            'discovery',
            'change',
          ])
          .optional(),
        limit: z.number().min(1).max(100).default(50),
      }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)
        const implementationId = tokenForQuery(ctx, args.implementationId)

        return zql.knowledgeObservations
          .where('workspaceId', workspaceId)
          .where('implementationId', implementationId)
          .where('type', args.type)
          .whereExists('workspace', (w) =>
            w.whereExists('members', (m) =>
              m.where('userId', userId).where('status', 'active'),
            ),
          )
          .orderBy('createdAt', 'desc')
          .limit(args.limit)
      },
    ),
  },
  friction: {
    /**
     * Inbox list: the workspace's friction items in the given statuses (all
     * when omitted), most reported first, with recent occurrence times for the trend. Repository and text
     * filtering happen client-side over this result.
     */
    list: defineQuery(
      z.object({
        workspaceId: z.string(),
        statuses: z
          .array(z.enum(['open', 'routed', 'fixing', 'resolved', 'dismissed']))
          .min(1)
          .optional(),
        scope: z.enum(['repo', 'environment', 'trevize']).optional(),
        /** Start of the trend window (epoch ms); see `frictionTrendSince`. */
        since: z.number(),
        limit: z.number().min(1).max(500).default(200),
      }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)

        return zql.frictionItems
          .where('workspaceId', workspaceId)
          .where(({ cmp }) =>
            args.statuses === undefined
              ? cmp('id', 'IS NOT', null)
              : cmp('status', 'IN', args.statuses),
          )
          .where(({ cmp }) =>
            args.scope === undefined
              ? cmp('id', 'IS NOT', null)
              : cmp('scope', args.scope),
          )
          .whereExists('workspace', (w) =>
            w.whereExists('members', (m) =>
              m.where('userId', userId).where('status', 'active'),
            ),
          )
          .related('occurrences', (q) =>
            q.where('createdAt', '>=', args.since).orderBy('createdAt', 'desc'),
          )
          .orderBy('occurrenceCount', 'desc')
          .orderBy('lastSeenAt', 'desc')
          .limit(args.limit)
      },
    ),
    byId: defineQuery(z.object({ id: z.string() }), ({ ctx, args }) => {
      const userId = userIdForQuery(ctx)
      const id = tokenForQuery(ctx, args.id)

      return zql.frictionItems
        .where('id', id)
        .whereExists('workspace', (w) =>
          w.whereExists('members', (m) =>
            m.where('userId', userId).where('status', 'active'),
          ),
        )
        .related('occurrences', (q) =>
          q
            .orderBy('createdAt', 'desc')
            .limit(FRICTION_DETAIL_OCCURRENCES)
            .related('run', (r) => r.one())
            .related('reportedBy', (u) => u.one()),
        )
        .related('fixRun', (r) => r.one())
        .one()
    }),
  },
  briefs: {
    list: defineQuery(
      z.object({
        workspaceId: z.string(),
        filter: z.enum(['active', 'archived']).default('active'),
        search: z.string().optional(),
        limit: z.number().min(1).max(500).default(20),
      }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)
        const search =
          args.search && args.search.trim().length > 0
            ? `%${args.search.trim()}%`
            : undefined

        return zql.briefs
          .where('workspaceId', workspaceId)
          .where(({ cmp, not }) =>
            args.filter === 'archived'
              ? cmp('status', 'archived')
              : not(cmp('status', 'archived')),
          )
          .where(({ cmp }) =>
            search === undefined
              ? cmp('id', 'IS NOT', null)
              : cmp('title', 'ILIKE', search),
          )
          .whereExists('workspace', (w) =>
            w.whereExists('members', (m) =>
              m.where('userId', userId).where('status', 'active'),
            ),
          )
          .orderBy('updatedAt', 'desc')
          .limit(args.limit)
      },
    ),
    byId: defineQuery(z.object({ id: z.string() }), ({ ctx, args }) => {
      const userId = userIdForQuery(ctx)
      const id = tokenForQuery(ctx, args.id)

      return zql.briefs
        .where('id', id)
        .whereExists('workspace', (w) =>
          w.whereExists('members', (m) =>
            m.where('userId', userId).where('status', 'active'),
          ),
        )
        .one()
    }),
    listRecent: defineQuery(
      z.object({
        workspaceId: z.string(),
        limit: z.number().min(1).max(50).default(5),
      }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)

        return zql.briefs
          .where('workspaceId', workspaceId)
          .where(({ cmp, not }) => not(cmp('status', 'archived')))
          .whereExists('workspace', (w) =>
            w.whereExists('members', (m) =>
              m.where('userId', userId).where('status', 'active'),
            ),
          )
          .orderBy('updatedAt', 'desc')
          .limit(args.limit)
      },
    ),
  },
  briefResources: {
    list: defineQuery(
      z.object({
        briefId: z.string(),
        type: z
          .enum(['github_repository', 'linear_project', 'linear_issue'])
          .optional(),
      }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const briefId = tokenForQuery(ctx, args.briefId)

        return zql.briefResources
          .where('briefId', briefId)
          .where('type', args.type)
          .whereExists('brief', (q) =>
            q.whereExists('workspace', (w) =>
              w.whereExists('members', (m) =>
                m.where('userId', userId).where('status', 'active'),
              ),
            ),
          )
      },
    ),
  },
  preview: {
    /**
     * Preview sessions for an implementation or collaborative run. Process
     * rows are loaded separately via {@link preview.listProcesses} — nested
     * `related()` was observed to return empty child collections for this
     * table, which hid the process switcher.
     */
    listSessions: defineQuery(
      z.object({
        workspaceId: z.string(),
        implementationId: z.string().optional(),
        runId: z.string().optional(),
      }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)
        const implementationId = args.implementationId
          ? tokenForQuery(ctx, args.implementationId)
          : undefined
        const runId = args.runId ? tokenForQuery(ctx, args.runId) : undefined

        let query = zql.implementationPreviewSessions
          .where('workspaceId', workspaceId)
          .whereExists('workspace', (w) =>
            w.whereExists('members', (m) =>
              m.where('userId', userId).where('status', 'active'),
            ),
          )

        if (runId) {
          query = query.where('runId', runId)
        } else if (implementationId) {
          query = query.where('implementationId', implementationId)
        } else {
          query = query.where('id', NO_ACCESS)
        }

        return query.orderBy('createdAt', 'asc')
      },
    ),
    /**
     * Preview processes for sessions belonging to a run or implementation.
     * Authz walks session → workspace → active membership.
     */
    listProcesses: defineQuery(
      z.object({
        workspaceId: z.string(),
        implementationId: z.string().optional(),
        runId: z.string().optional(),
      }),
      ({ ctx, args }) => {
        const userId = userIdForQuery(ctx)
        const workspaceId = workspaceIdForQuery(ctx, args.workspaceId)
        const implementationId = args.implementationId
          ? tokenForQuery(ctx, args.implementationId)
          : undefined
        const runId = args.runId ? tokenForQuery(ctx, args.runId) : undefined

        return zql.implementationPreviewProcesses
          .whereExists('implementationPreviewSession', (session) => {
            let scoped = session
              .where('workspaceId', workspaceId)
              .whereExists('workspace', (w) =>
                w.whereExists('members', (m) =>
                  m.where('userId', userId).where('status', 'active'),
                ),
              )

            if (runId) {
              scoped = scoped.where('runId', runId)
            } else if (implementationId) {
              scoped = scoped.where('implementationId', implementationId)
            } else {
              scoped = scoped.where('id', NO_ACCESS)
            }

            return scoped
          })
          .orderBy('createdAt', 'asc')
      },
    ),
  },
})

export { schema }
